import { Doc } from '@dadohr/shared/domainTypes/document';
import { CustomUserField } from '@dadohr/shared/domainTypes/org';
import { IUser, StandardUserField } from '@dadohr/shared/domainTypes/user';
import { Typography } from '@material-ui/core';
import { compact, get, orderBy, uniq } from 'lodash';
import { useMemo } from 'react';
import { useUnsafeTranslation } from '../../i18next';
import { useMappedLoadingValue } from '../../services/firebase';
import {
  getUsersByOrgId,
  matchUserBySearchTerm,
  toFullName,
  toSearchTermRegexp,
  UNKNOWN_USER_2,
  useUsersByOrgIdAsMap
} from '../../services/user';
import {
  AutocompleteMulti,
  AutocompleteMultiFreeSolo,
  AutocompleteSingle
} from '../Autocomplete';
import { InputFieldLoader } from '../InputFieldLoader';

// TODO AutcompleteUser multi
// TODO versions that operate on ids only

export const AutocompleteUser = ({
  label,
  orgId,
  value,
  error,
  helperText,
  disabled,
  onChange
}: {
  label: string;
  error?: boolean | undefined;
  helperText?: React.ReactNode;
  disabled?: boolean | undefined;
  orgId: string;
  value: Doc<IUser> | null;
  onChange: (nextValue: Doc<IUser> | null) => void;
}) => {
  return (
    <AutocompleteSingle
      key={value?.id}
      value={value}
      error={error}
      helperText={helperText}
      disabled={disabled}
      onChange={onChange}
      options={() =>
        getUsersByOrgId(orgId).then((us) =>
          orderBy(
            us,
            [(u) => u.data.lastName, (u) => u.data.preferredName],
            'asc'
          )
        )
      }
      getOptionLabel={(o) => toFullName(o.data)}
      getOptionSelected={(o, v) => o?.id === v?.id}
      renderOption={(o) => {
        return (
          <div>
            <div>{toFullName(o.data)}</div>
            <Typography variant="caption">
              {[o.data.role, o.data.department, o.data.location]
                .filter((x) => x)
                .join(' • ')}
            </Typography>
          </div>
        );
      }}
      filterOptions={(os, s) => {
        const re = toSearchTermRegexp(s.inputValue);
        return os.filter((o) => matchUserBySearchTerm(re, o));
      }}
      label={label}
      fullWidth
    />
  );
};

export const AutocompleteUserMultiById = ({
  label,
  orgId,
  value,
  error,
  helperText,
  disabled,
  onChange,
  autoFocus,
  openOnFocus,
  fullWidth
}: {
  label: string;
  error?: boolean | undefined;
  helperText?: React.ReactNode;
  disabled?: boolean | undefined;
  orgId: string;
  value: string[];
  onChange: (nextValue: string[]) => void;
  autoFocus?: boolean;
  openOnFocus?: boolean;
  fullWidth?: boolean;
}) => {
  const { t } = useUnsafeTranslation();
  const [v] = useMappedLoadingValue(useUsersByOrgIdAsMap(orgId), (us) => {
    const userList = orderBy(
      Object.values(us),
      [(u) => u.data.lastName, (u) => u.data.preferredName],
      'asc'
    );
    return { userMap: us, userList };
  });
  if (!v) {
    return <InputFieldLoader label={label} fullWidth={fullWidth} />;
  }
  const selectedUsers = value.map(
    (userId) => v.userMap[userId] || UNKNOWN_USER_2(orgId, userId, t)
  );
  return (
    <AutocompleteMulti
      value={selectedUsers}
      error={error}
      helperText={helperText}
      onChange={(nextUsers) => onChange(nextUsers.map((u) => u.id))}
      options={v.userList}
      openOnFocus={openOnFocus}
      autoFocus={autoFocus}
      getOptionLabel={(o) => toFullName(o.data)}
      getOptionSelected={(o, v) => o?.id === v?.id}
      renderOption={(o) => {
        return (
          <div>
            <div>{toFullName(o.data)}</div>
            <Typography variant="caption">
              {[o.data.role, o.data.department, o.data.location]
                .filter((x) => x)
                .join(' • ')}
            </Typography>
          </div>
        );
      }}
      filterOptions={(os, s) => {
        // also take out already selected users
        const re = toSearchTermRegexp(s.inputValue);
        return os.filter(
          (o) => !value.includes(o.id) && matchUserBySearchTerm(re, o)
        );
      }}
      label={label}
      fullWidth={fullWidth}
    />
  );
};

// ONLY SUPPORTS TEXT FIELDS AT THE MOMENT
export const AutocompleteUserFieldMulti = ({
  field,
  users,
  ...other
}: {
  value: string[];
  onChange: (nextValue: string[]) => void;

  field: StandardUserField | CustomUserField;
  users: Doc<IUser>[];

  label?: string;
  autoFocus?: boolean;
  openOnFocus?: boolean;
  fullWidth?: boolean;
}) => {
  const options = useMemo<string[]>(() => {
    if (field.type !== 'string') {
      return [];
    }
    return uniq(
      compact(users.map((u) => get(u.data, field.key) || null))
    ).sort();
  }, [users, field]);
  return <AutocompleteMultiFreeSolo {...other} options={options} />;
};
